import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  id: '01',
  question: 'What types of tasks do you handle?',
  answer: 'We work with operational, technical, organizational, and documentation-related tasks across marine, energy, and industrial projects.'
}, {
  id: '02',
  question: 'Who can contact ELVORA?',
  answer: 'Shipowners, technical managers, operators, and industrial project leads can contact us for support with their operational challenges.'
}, {
  id: '03',
  question: 'Do you only work with offshore projects?',
  answer: 'No, we work with both offshore and onshore projects across marine, energy, and industrial sectors.'
}, {
  id: '04',
  question: 'How do you start working on a request?',
  answer: 'We begin with a brief consultation to understand your needs, then provide a clear proposal with timeline and deliverables.'
}, {
  id: '05',
  question: 'Is the information confidential?',
  answer: 'Yes, all information is treated with strict confidentiality. We adhere to NDA protocols and data protection standards.'
}];
export function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);
  return <section className="w-full py-16 px-6 md:px-12 bg-[var(--color-bg)]" data-id="element-71">
      <div className="max-w-7xl mx-auto" data-id="element-72">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-73">
          {/* Left side */}
          <div className="lg:w-2/5" data-id="element-74">
            {/* FAQ pill */}
            <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-75">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-76">
                FAQ
              </span>
            </div>

            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-77">
              Have a Question?
            </h2>
            <p className="text-[var(--color-text-light)] text-sm mb-6" data-id="element-78">
              For quick questions or clarifications, you can contact us via
              messenger.
            </p>

            {/* Mobile: FAQ first, then contacts */}
            <div className="lg:hidden mb-8" data-id="element-79">
              <div className="bg-white rounded-2xl p-6 border border-gray-200" data-id="element-80">
                <div className="space-y-0" data-id="element-81">
                  {faqs.map(faq => <div key={faq.id} className="border-b border-gray-100 last:border-0" data-id="element-82">
                      <button onClick={() => setOpenId(openId === faq.id ? null : faq.id)} className="w-full py-4 flex items-start justify-between text-left gap-4" data-id="element-83">
                        <div className="flex gap-3 flex-1" data-id="element-84">
                          <span className="text-[var(--color-text-light)] font-medium text-sm flex-shrink-0" data-id="element-85">
                            {faq.id}
                          </span>
                          <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-86">
                            {faq.question}
                          </span>
                        </div>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-87">
                          <svg className={`w-3.5 h-3.5 transition-colors ${openId === faq.id ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-88">
                            {openId === faq.id ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-89" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-90" />}
                          </svg>
                        </div>
                      </button>
                      <AnimatePresence data-id="element-91">
                        {openId === faq.id && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-92">
                            <p className="pl-8 pb-4 text-sm text-[var(--color-text-light)]" data-id="element-93">
                              {faq.answer}
                            </p>
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Contact Buttons - Icon + Text Centered */}
            <div className="space-y-3" data-id="element-94">
              {/* Telegram */}
              <a href="https://t.me/elvora" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-95">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" data-id="element-96">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#0088cc" data-id="element-97" />
                </svg>
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-98">
                  Telegram
                </span>
              </a>

              {/* Viber - Correct Icon */}
              <a href="viber://chat?number=+380" className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-99">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" data-id="element-100">
                  <path d="M11.398 0c-2.05.027-6.547.362-9.08 2.66C.88 4.6.2 7.333.13 10.732c-.07 3.398-.16 9.764 5.998 11.475h.004l-.004 2.82s-.04 1.042.65 1.256c.83.258 1.316-.534 2.11-1.39.434-.47 1.035-1.156 1.49-1.685 4.105.345 7.26-.444 7.627-.56.827-.27 5.52-.872 6.283-7.098.788-6.418-.383-10.48-2.495-12.308C21.14.586 18.508-.245 11.398 0zm.062 1.804c6.45.022 8.868 1.87 9.45 2.477 1.725 1.492 2.7 5.084 1.993 10.698-.655 5.203-4.39 5.524-5.066 5.75-.307.103-3.09.784-6.77.512 0 0-2.687 3.24-3.52 4.073-.13.138-.277.182-.373.16-.14-.032-.177-.192-.176-.426l.02-4.872c-5.225-1.387-4.927-6.696-4.867-9.487.06-2.792.6-5.062 2.145-6.64C5.95 1.12 10.11.807 11.46.805z" fill="#665CAC" data-id="element-101" />
                  <path d="M11.067 4.01c.213.004.4.018.603.032.528.037.928.094 1.322.234.74.262 1.322.714 1.727 1.343.294.458.495.984.6 1.56.066.373.087.753.074 1.13-.007.224-.185.405-.41.412-.223.008-.408-.17-.417-.394-.013-.288-.04-.576-.094-.857-.092-.472-.245-.917-.498-1.31-.3-.473-.742-.8-1.305-.983-.33-.107-.674-.163-1.024-.184-.224-.014-.41-.18-.418-.405-.01-.234.16-.415.384-.43.106-.006.203-.01.298-.01.075 0 .15.003.224.007zm.34 1.79c.23.014.416.192.418.425.002.23-.18.423-.41.437-.55.032-.954.245-1.223.558-.194.227-.312.49-.357.79-.026.17-.032.34-.022.512.01.227-.165.423-.392.435-.227.01-.423-.165-.435-.392-.015-.234-.01-.47.023-.703.067-.458.227-.874.498-1.24.427-.578 1.026-.906 1.773-.983.043-.003.085-.004.126-.006zm.315 1.772c.247.014.43.213.418.46-.012.246-.213.43-.46.418-.415-.02-.693.15-.82.46-.066.162-.09.332-.072.504.015.138-.042.277-.157.362-.115.085-.277.096-.403.024-.128-.07-.202-.21-.19-.354.024-.31.107-.597.286-.843.298-.416.73-.622 1.263-.655.045-.003.09-.005.136-.006z" fill="#665CAC" data-id="element-102" />
                </svg>
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-103">
                  Viber
                </span>
              </a>

              {/* WhatsApp */}
              <a href="https://wa.me/380" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-104">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" data-id="element-105">
                  <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" fill="#25D366" data-id="element-106" />
                  <path d="M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z" fill="white" data-id="element-107" />
                </svg>
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-108">
                  WhatsApp
                </span>
              </a>

              {/* Email */}
              <a href="mailto:info@elvora.com" className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-109">
                <svg className="w-6 h-6 text-[var(--color-tan)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-110">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-111" />
                </svg>
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-112">
                  Email
                </span>
              </a>
            </div>
          </div>

          {/* Right side - FAQ accordion (Desktop only) */}
          <div className="hidden lg:block lg:w-3/5" data-id="element-113">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200" data-id="element-114">
              <div className="space-y-0" data-id="element-115">
                {faqs.map(faq => <div key={faq.id} className="border-b border-gray-100 last:border-0" data-id="element-116">
                    <button onClick={() => setOpenId(openId === faq.id ? null : faq.id)} className="w-full py-5 flex items-start justify-between text-left gap-4" data-id="element-117">
                      <div className="flex gap-4 flex-1" data-id="element-118">
                        <span className="text-[var(--color-text-light)] font-medium text-sm flex-shrink-0" data-id="element-119">
                          {faq.id}
                        </span>
                        <span className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-120">
                          {faq.question}
                        </span>
                      </div>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-121">
                        <svg className={`w-4 h-4 transition-colors ${openId === faq.id ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-122">
                          {openId === faq.id ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-123" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-124" />}
                        </svg>
                      </div>
                    </button>
                    <AnimatePresence data-id="element-125">
                      {openId === faq.id && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-126">
                          <p className="pl-10 pb-5 text-sm text-[var(--color-text-light)]" data-id="element-127">
                            {faq.answer}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}